import { DECISION_NOTIFICATION_TYPES, NOTIFICATION_TYPES, type Client } from '@optimizely/optimizely-sdk';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

type TrackDecisionPayload = {
  type: string;
  decisionInfo: {
    variableValues: { [key: string]: unknown };
    sourceInfo: {
      experimentKey: string;
      variationKey: string;
    };
  };
};

export default defineNuxtPlugin(() => {
  const { $optimizely } = useNuxtApp();

  if (!$optimizely) {
    console.warn('Optimizely is not initialized');
    return;
  }

  $optimizely.notificationCenter.addNotificationListener(NOTIFICATION_TYPES.DECISION, (decision: unknown) => {
    onNuxtReady(() => {
      trackDecision(decision as TrackDecisionPayload, $optimizely);
    });
  });
});

function warnAboutMissingDimensionNumber(experimentKey: string) {
  const isNotProduction = useRuntimeConfig().public.STAGE !== 'production';
  const message = `"${experimentKey}" is missing Optimizely dimension number.`;

  if (isNotProduction) {
    throw new Error(message);
  }
}

function trackDecision({ type, decisionInfo }: TrackDecisionPayload, client: Client) {
  const clientConfig = client.getOptimizelyConfig();

  if (type !== DECISION_NOTIFICATION_TYPES.ALL_FEATURE_VARIABLES || !clientConfig) return;

  const { variableValues, sourceInfo } = decisionInfo;
  const experimentKey = sourceInfo.experimentKey;
  const variationKey = sourceInfo.variationKey;

  if (!clientConfig.experimentsMap[experimentKey]) return;

  const experimentId = clientConfig.experimentsMap[experimentKey].id;
  const variationId = clientConfig.experimentsMap[experimentKey]?.variationsMap?.[variationKey]?.id;

  const optimizelyDimensionNumber = variableValues ? variableValues['optimizely-dimension-number'] : undefined;

  if (!optimizelyDimensionNumber) {
    warnAboutMissingDimensionNumber(experimentKey);
    return;
  }

  if (optimizelyDimensionNumber && variationKey && experimentId) {
    const hasEventAlready = window?.dataLayer?.find(
      (item) =>
        item.event === 'campaign-decided' &&
        item['optimizely-dimension-value'] === `${experimentKey} (${experimentId}):${variationKey} (${variationId})` &&
        item['optimizely-dimension-number'] === optimizelyDimensionNumber,
    );

    if (hasEventAlready) return;

    // Add data layer if not present
    if (!Array.isArray(window.dataLayer) && typeof window.dataLayer === 'undefined') {
      window.dataLayer = [];
    }

    // Push data to window
    return window.dataLayer.push({
      event: 'campaign-decided',
      'optimizely-dimension-value': `${experimentKey} (${experimentId}):${variationKey} (${variationId})`,
      'optimizely-dimension-number': optimizelyDimensionNumber,
    });
  }
}
